import { useCallback, useState, FormEventHandler, ComponentType } from 'react';

import { payment } from '../common/validation';
import { TextField, TextFieldProps } from '../text-field';

export type CardNetwork =
  | 'visa'
  | 'mastercard'
  | 'discover'
  | 'amex'
  | 'jcb'
  | 'dinersclub'
  | 'maestro'
  | 'laser'
  | 'unionpay'
  | 'elo'
  | 'hipercard';

export type CardNumberFieldEndAdornmentSlotProps = {
  network: CardNetwork | null;
};

type CardNumberFieldProps = TextFieldProps & {
  endAdornmentSlot?: ComponentType<CardNumberFieldEndAdornmentSlotProps>;
  onNetworkChange?: (network: CardNetwork | null) => void;
};

export const CardNumberField = (props: CardNumberFieldProps) => {
  const {
    endAdornmentSlot: EndAdornmentSlot,
    onNetworkChange,
    InputProps,
    inputProps,
    fieldProps,
    label,
    ...rest
  } = props;

  const [network, setNetwork] = useState<CardNetwork | null>(null);

  /**
   * Attaches Payment to input element. Since we don't need a reference to the `<input />` after the initial mount, use
   * the classic callback-style ref pattern rather than the newer `useRef()` approach.
   *
   * We can probably extract this to a `usePaymentRef()` hook if we use this lib to drive other payment-related fields
   * such as expiry and CVV.
   */
  const attachFormatter = useCallback((input?: HTMLInputElement) => {
    if (!input) {
      return;
    }
    payment.formatCardNumber(input);
  }, []);

  /**
   * Payment exposes card network as a classname on the `<input />` element. That would require a DOM query on every
   * input event, so track it in state instead.
   */

  const controlledOnChange = inputProps?.onChange;

  const handleChange: FormEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      controlledOnChange?.(event);

      const detectedNetwork = payment.fns.cardType(event.currentTarget.value) as CardNetwork | null;

      if (detectedNetwork === network) {
        return;
      }

      setNetwork(detectedNetwork);
      onNetworkChange?.(detectedNetwork);
    },
    [controlledOnChange, network, onNetworkChange],
  );

  return (
    <TextField
      fullWidth
      type="text"
      autoComplete="on"
      inputProps={{
        ...inputProps,
        inputMode: 'numeric',
        autoComplete: 'on',
        autocompletetype: 'cc-number',
        pattern: '\\d*',
        onChange: handleChange,
      }}
      // eslint-disable-next-line react/jsx-no-duplicate-props
      InputProps={{
        ...InputProps,
        endAdornment: EndAdornmentSlot ? <EndAdornmentSlot network={network} /> : InputProps?.endAdornment,
      }}
      fieldProps={{
        ...fieldProps,
        format: payment.fns.formatCardNumber,
        formatOnBlur: true,
      }}
      label={label ?? 'Card Number'}
      inputRef={attachFormatter}
      {...rest}
    />
  );
};
